/**
 * Rule Build Chat Slice
 *
 * Redux slice for managing rule build chat state, messages, and interrupt workflow.
 * Uses the shared slice factory from ai-chat-core with rule-build specific extensions.
 */

import {
  type ActivityType,
  type AssistantStatus,
  type ChatMessage,
  type ChoiceOption,
  type ConfirmationNamespace,
  type ConfirmationType,
  createInitialState,
  type EditPreview,
  type InterruptEventType,
  type SSEPerformanceMetrics,
  type StepPayload,
} from '@features/ai-chat-core';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// ============================================================================
// Types
// ============================================================================

/**
 * Rule Build specific state
 */
export interface RuleBuildChatState {
  // Session
  threadId: string | null;
  taskId: string | null;

  // Messages
  messages: ChatMessage[];

  // Activity status messages
  activities: Record<
    string,
    {
      messageId: string;
      activityType: ActivityType;
      message: string;
      timestamp: number;
    }
  >;

  // Streaming state
  assistant: {
    status: AssistantStatus;
    currentStep: string | null;
    streamingContent: string;
    currentMessageId: string | null;
    firstTokenReceived: boolean;
  };

  // Entity (draft) streaming updates
  entityUpdates: Record<string, string>;
  streamingEntities: string[];
  entityPreviousContent: Record<string, string>;
  affectedEntityIds: string[];

  // Unified interrupt state
  interrupt: {
    isActive: boolean;
    interruptId: string;
    eventType: InterruptEventType;
    // CONFIRMATION fields
    confirmLabel?: string;
    cancelLabel?: string;
    message?: string;
    edits?: EditPreview[];
    namespace?: ConfirmationNamespace;
    confirmationType?: ConfirmationType;
    // MULTIPLE_CHOICE fields
    question?: string;
    options?: ChoiceOption[];
    selectedOptionIds: string[];
    allowMultiple?: boolean;
    allowSelectAll?: boolean;
    minSelections?: number;
    maxSelections?: number;
    allowTextInput?: boolean;
    textInputPlaceholder?: string;
    // MULTI_STEP fields
    steps?: StepPayload[];
    title?: string;
    currentStepIndex?: number;
    stepResponses?: Record<
      string,
      {
        stepId: string;
        selectedIds?: string[];
        confirmed?: boolean;
      }
    >;
    // EXPRESS_INTEREST fields
    detectedIntent?: string;
    originalMessage?: string;
    metadata?: unknown | null;
  } | null;

  // UI state
  isChatVisible: boolean;

  // Error handling
  error: string | null;

  // SSE performance metrics
  ssePerformanceMetrics: SSEPerformanceMetrics | null;

  // Rule Build specific fields
  focusedDraftId: string | null;
  needsTaskRefetch: boolean;
}

// ============================================================================
// Initial State
// ============================================================================

const baseInitialState = createInitialState();

const initialState: RuleBuildChatState = {
  ...baseInitialState,
  // Rule Build specific fields
  taskId: null,
  focusedDraftId: null,
  needsTaskRefetch: false,
};

// ============================================================================
// Slice
// ============================================================================

export const ruleBuildChatSlice = createSlice({
  name: 'ruleBuild/chat',
  initialState,
  reducers: {
    // ========================================================================
    // Session Management
    // ========================================================================

    initializeChat: (state, action: PayloadAction<{ taskId: string }>) => {
      state.taskId = action.payload.taskId;
      state.threadId = null;
      state.messages = [];
      state.error = null;
      state.assistant.status = 'idle';
      state.entityUpdates = {};
      state.streamingEntities = [];
      state.entityPreviousContent = {};
      state.affectedEntityIds = [];
      state.focusedDraftId = null;
      state.needsTaskRefetch = false;
    },

    setThreadId: (state, action: PayloadAction<string>) => {
      state.threadId = action.payload;
    },

    setTaskId: (state, action: PayloadAction<string | null>) => {
      state.taskId = action.payload;
    },

    // ========================================================================
    // Message Management
    // ========================================================================

    addUserMessage: (state, action: PayloadAction<string>) => {
      const now = new Date();
      state.messages.push({
        id: `user-${now.getTime()}`,
        role: 'user',
        content: action.payload,
        timestamp: now.toISOString(),
      });
    },

    // ========================================================================
    // Streaming State
    // ========================================================================

    setAssistantStatus: (state, action: PayloadAction<AssistantStatus>) => {
      state.assistant.status = action.payload;
    },

    setCurrentStep: (state, action: PayloadAction<string>) => {
      state.assistant.currentStep = action.payload;
    },

    startStreamingMessage: (state, action: PayloadAction<string>) => {
      state.assistant.currentMessageId = action.payload;
      state.assistant.streamingContent = '';
      state.assistant.firstTokenReceived = false;
      state.assistant.status = 'streaming';
    },

    appendStreamingContent: (state, action: PayloadAction<string>) => {
      state.assistant.streamingContent += action.payload;
      if (!state.assistant.firstTokenReceived) {
        state.assistant.firstTokenReceived = true;
      }
    },

    finalizeMessage: state => {
      if (state.assistant.streamingContent) {
        const now = new Date();
        state.messages.push({
          id: state.assistant.currentMessageId || `assistant-${now.getTime()}`,
          role: 'assistant',
          content: state.assistant.streamingContent,
          timestamp: now.toISOString(),
        });
      }
      state.assistant.streamingContent = '';
      state.assistant.currentMessageId = null;
      state.assistant.firstTokenReceived = false;
      state.assistant.status = 'thinking';
      // Clear activities when message is finalized
      state.activities = {};
    },

    // ========================================================================
    // Activity Management
    // ========================================================================

    setActivitySnapshot: (
      state,
      action: PayloadAction<{
        messageId: string;
        activityType: ActivityType;
        message: string;
        timestamp: number;
        replace?: boolean;
      }>,
    ) => {
      const { messageId, activityType, message, timestamp, replace = true } = action.payload;

      if (!replace && state.activities[messageId]) {
        return;
      }

      state.activities[messageId] = {
        messageId,
        activityType,
        message,
        timestamp,
      };
    },

    clearActivity: (state, action: PayloadAction<string>) => {
      const messageId = action.payload;
      const { [messageId]: _removed, ...remaining } = state.activities;
      state.activities = remaining;
    },

    clearAllActivities: state => {
      state.activities = {};
    },

    // ========================================================================
    // Entity (Draft) Streaming
    // ========================================================================

    startEntityStreaming: (state, action: PayloadAction<string>) => {
      const entityId = action.payload;
      state.entityUpdates[entityId] = '';
      if (!state.streamingEntities.includes(entityId)) {
        state.streamingEntities.push(entityId);
      }
    },

    appendEntityContent: (
      state,
      action: PayloadAction<{ entityId: string; content: string }>,
    ) => {
      const { entityId, content } = action.payload;
      if (state.entityUpdates[entityId] !== undefined) {
        state.entityUpdates[entityId] += content;
      } else {
        state.entityUpdates[entityId] = content;
      }
    },

    finalizeEntityStreaming: (state, action: PayloadAction<string>) => {
      const entityId = action.payload;
      state.streamingEntities = state.streamingEntities.filter(id => id !== entityId);
    },

    clearAllEntityUpdates: state => {
      state.entityUpdates = {};
      state.streamingEntities = [];
    },

    markEntityAffected: (state, action: PayloadAction<string>) => {
      const entityId = action.payload;
      if (!state.affectedEntityIds.includes(entityId)) {
        state.affectedEntityIds.push(entityId);
      }
    },

    clearAffectedEntity: (state, action: PayloadAction<string>) => {
      const entityId = action.payload;
      state.affectedEntityIds = state.affectedEntityIds.filter(id => id !== entityId);
    },

    // ========================================================================
    // Interrupt Management
    // ========================================================================

    showInterrupt: (
      state,
      action: PayloadAction<{
        interruptId: string;
        eventType: InterruptEventType;
        confirmLabel?: string;
        cancelLabel?: string;
        message?: string;
        edits?: EditPreview[];
        namespace?: ConfirmationNamespace;
        confirmationType?: ConfirmationType;
        question?: string;
        options?: ChoiceOption[];
        allowMultiple?: boolean;
        allowSelectAll?: boolean;
        minSelections?: number;
        maxSelections?: number;
        allowTextInput?: boolean;
        textInputPlaceholder?: string;
        steps?: StepPayload[];
        title?: string;
        detectedIntent?: string;
        originalMessage?: string;
        metadata?: unknown | null;
      }>,
    ) => {
      const { interruptId, eventType, ...fields } = action.payload;

      state.interrupt = {
        isActive: true,
        interruptId,
        eventType,
        selectedOptionIds: [],
        currentStepIndex: eventType === 'MULTI_STEP' ? 0 : undefined,
        stepResponses: eventType === 'MULTI_STEP' ? {} : undefined,
        ...fields,
      };

      if (eventType === 'CONFIRMATION') {
        state.assistant.status = 'awaiting_confirmation';
      } else {
        state.assistant.status = 'awaiting_selection';
      }
    },

    toggleOptionSelection: (state, action: PayloadAction<string>) => {
      if (!state.interrupt) return;

      const optionId = action.payload;
      const selectedIds = state.interrupt.selectedOptionIds;

      if (state.interrupt.eventType === 'CONFIRMATION') {
        state.interrupt.selectedOptionIds = [optionId];
        return;
      }

      if (selectedIds.includes(optionId)) {
        state.interrupt.selectedOptionIds = selectedIds.filter(id => id !== optionId);
      } else {
        state.interrupt.selectedOptionIds.push(optionId);
      }
    },

    selectAllOptions: state => {
      if (!state.interrupt || state.interrupt.eventType !== 'MULTIPLE_CHOICE') return;
      if (!state.interrupt.options) return;

      state.interrupt.selectedOptionIds = state.interrupt.options.map(opt => opt.id);
    },

    deselectAllOptions: state => {
      if (!state.interrupt || state.interrupt.eventType !== 'MULTIPLE_CHOICE') return;

      state.interrupt.selectedOptionIds = [];
    },

    clearInterrupt: state => {
      state.interrupt = null;
    },

    // ========================================================================
    // Rule Build Specific
    // ========================================================================

    setFocusedDraftId: (state, action: PayloadAction<string | null>) => {
      state.focusedDraftId = action.payload;
    },

    setNeedsTaskRefetch: (state, action: PayloadAction<boolean>) => {
      state.needsTaskRefetch = action.payload;
    },

    // ========================================================================
    // UI State Management
    // ========================================================================

    setChatVisibility: (state, action: PayloadAction<boolean>) => {
      state.isChatVisible = action.payload;
    },

    toggleChatVisibility: state => {
      state.isChatVisible = !state.isChatVisible;
    },

    // ========================================================================
    // Workflow Completion
    // ========================================================================

    completeWorkflow: state => {
      state.assistant.status = 'idle';
      state.assistant.currentStep = null;
      state.interrupt = null;
      state.streamingEntities = [];
      // Clear activities (status indicators like "Thinking...")
      state.activities = {};
      // Flag that we need to refetch task to see updated drafts
      state.needsTaskRefetch = true;
    },

    // ========================================================================
    // Error Handling
    // ========================================================================

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.assistant.status = 'idle';
      state.entityUpdates = {};
      state.streamingEntities = [];
    },

    clearError: state => {
      state.error = null;
    },

    // ========================================================================
    // SSE Performance Tracking
    // ========================================================================

    setSSEPerformanceMetrics: (state, action: PayloadAction<SSEPerformanceMetrics>) => {
      state.ssePerformanceMetrics = action.payload;
    },

    clearSSEPerformanceMetrics: state => {
      state.ssePerformanceMetrics = null;
    },

    // ========================================================================
    // Reset
    // ========================================================================

    resetChat: () => initialState,
  },
});

// Export actions
export const {
  initializeChat,
  setThreadId,
  setTaskId,
  addUserMessage,
  setAssistantStatus,
  setCurrentStep,
  startStreamingMessage,
  appendStreamingContent,
  finalizeMessage,
  setActivitySnapshot,
  clearActivity,
  clearAllActivities,
  startEntityStreaming,
  appendEntityContent,
  finalizeEntityStreaming,
  clearAllEntityUpdates,
  markEntityAffected,
  clearAffectedEntity,
  showInterrupt,
  toggleOptionSelection,
  selectAllOptions,
  deselectAllOptions,
  clearInterrupt,
  setFocusedDraftId,
  setNeedsTaskRefetch,
  setChatVisibility,
  toggleChatVisibility,
  completeWorkflow,
  setError,
  clearError,
  setSSEPerformanceMetrics,
  clearSSEPerformanceMetrics,
  resetChat,
} = ruleBuildChatSlice.actions;

// Export reducer
export default ruleBuildChatSlice.reducer;
